/**
 * CRM App — crm.legacyinsights.com.br
 * Entrypoint exclusivo do módulo de CRM (Pipeline, Leads, Financeiro, etc.)
 */
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { useStore } from "@/store/useStore";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from '@/components/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import Auth from "@/pages/Auth";
import NotFound from "@/pages/NotFound";

// ── CRM Module Routes Only ──
import { crmRoutes, NPSPublicSurvey } from "@/modules/crm/routes";
import { sharedRoutes } from "@/modules/shared/routes";
import type { ModuleRoute } from "@/modules/crm/routes";

// ── Platform Pages ──
const Workspaces = lazy(() => import("@/pages/Workspaces"));
const InviteProject = lazy(() => import("@/pages/InviteProject"));
const JoinProject = lazy(() => import("@/pages/JoinProject"));
const PublicPage = lazy(() => import("@/pages/PublicPage"));
const PublicForm = lazy(() => import("@/pages/PublicForm"));
const PublicSchedule = lazy(() => import("@/pages/PublicSchedule"));
const PublicProposal = lazy(() => import("@/pages/PublicProposal"));
const AdminCMS = lazy(() => import("@/pages/AdminCMS"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const UpgradePlanos = lazy(() => import("@/pages/UpgradePlanos"));
const CancelSubscription = lazy(() => import("@/pages/CancelSubscription"));

const BILLING_ENABLED = import.meta.env.VITE_ENABLE_BILLING === 'true';

const allModuleRoutes: ModuleRoute[] = [...crmRoutes, ...sharedRoutes];

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error: unknown) => {
        if (error && typeof error === 'object' && 'status' in error && (error.status === 401 || error.status === 403)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: true,
    },
    mutations: { retry: 1 },
  },
});

function PageSpinner() {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ThemeInit() {
  const { theme } = useStore();
  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? '#0a0f1a' : '#fafafa');
  }, [theme]);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<any>(undefined);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s); setChecked(true);
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession((prev: any) => prev === undefined ? s : prev); setChecked(true);
    });
    return () => subscription.unsubscribe();
  }, []);
  if (!checked) return <div className="flex h-screen items-center justify-center bg-background"><div className="h-8 w-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" /></div>;
  // Leva o hash junto: um erro de OAuth chega em `/#error=…` e o Auth mostra o motivo.
  if (!session) return <Navigate to={{ pathname: '/auth', hash: window.location.hash || undefined }} replace />;
  return <>{children}</>;
}

function ProjectRoute({ children, allowedTypes }: { children: React.ReactNode, allowedTypes: Array<'crm'> }) {
  const { activeProjectId, activeProjectType } = useStore();
  if (!activeProjectId || !activeProjectType) return <Navigate to="/workspaces" replace />;
  if (!allowedTypes.includes(activeProjectType as 'crm')) return <Navigate to="/workspaces" replace />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ThemeInit />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/invite/:projectId" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><InviteProject /></Suspense></ErrorBoundary>} />
            <Route path="/join/:code" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><JoinProject /></Suspense></ErrorBoundary>} />
            <Route path="/workspaces" element={<ProtectedRoute><ErrorBoundary><Suspense fallback={<PageSpinner />}><Workspaces /></Suspense></ErrorBoundary></ProtectedRoute>} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/workspaces" replace />} />
              {allModuleRoutes.map(({ path, component: Component, allowedTypes }) => (
                <Route key={path} path={path} element={
                  <ProjectRoute allowedTypes={allowedTypes}>
                    <ErrorBoundary><Suspense fallback={<PageSpinner />}><Component /></Suspense></ErrorBoundary>
                  </ProjectRoute>
                } />
              ))}
              <Route path="/audit-log" element={<Navigate to="/configuracoes?tab=audit" replace />} />
              <Route path="/api-docs" element={<Navigate to="/configuracoes?tab=api" replace />} />
              {BILLING_ENABLED && (
                <>
                  <Route path="/upgrade" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><UpgradePlanos /></Suspense></ErrorBoundary>} />
                  <Route path="/cancelar" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><CancelSubscription /></Suspense></ErrorBoundary>} />
                </>
              )}
            </Route>
            <Route path="/admin" element={<ProtectedRoute><ErrorBoundary><Suspense fallback={<PageSpinner />}><AdminCMS /></Suspense></ErrorBoundary></ProtectedRoute>} />
            <Route path="/p/:slug" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><PublicPage /></Suspense></ErrorBoundary>} />
            <Route path="/form/:slug" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><PublicForm /></Suspense></ErrorBoundary>} />
            <Route path="/schedule/:username" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><PublicSchedule /></Suspense></ErrorBoundary>} />
            <Route path="/proposal/:token" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><PublicProposal /></Suspense></ErrorBoundary>} />
            <Route path="/nps-survey/:projectId" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><NPSPublicSurvey /></Suspense></ErrorBoundary>} />
            <Route path="/nps-survey/:projectId/:clientId" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><NPSPublicSurvey /></Suspense></ErrorBoundary>} />
            <Route path="/portal/:slug" element={<ErrorBoundary><Suspense fallback={<PageSpinner />}><ClientPortal /></Suspense></ErrorBoundary>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
